import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'components/DataTable';
import qs from 'query-string';
import { DATE_FORMATS, ORDER_STATUS, formatAddress, formatDate, getPageLimit, showError, showSuccess, updatePageLimit } from 'utils';
import { tableStyles } from '../../../css/tableStyles';
import styles from './index.module.scss';
import ActionButton from 'components/ActionButton';
import { fetchOrders, markOrderAsDelivered, markOrderAsPacked, removeOrderFromRoute } from 'services/api/orders';
import RouteInfoTooltip from './RouteInfoToolTip';
import AddOrderToRouteModal from 'components/AddOrderToRouteModal';
import RowActionComponent from 'components/RowActionComponent';
import Filter from './Filter';
import moment from 'moment';
import Currency from 'components/Currency/Currency';
import CustomLink from 'components/TableLink';
import DashboardPage from 'pages/dashboard';

let filters: { [p: string]: any; page: number } = {
  page: 0,
  startDate: moment().subtract(30, 'days').toISOString(),
  endDate: moment().toISOString(),
};

const Orders = props => {
  const [loading, setLoading] = useState<boolean>(false);
  const [orders, setOrders] = useState<Record<string, any>>({});
  const [tooltipData, setTooltipData] = useState<Record<string, any>>({});
  const [renderTooltip, setRenderTooltip] = useState<boolean>(false);
  const [addToRouteModalOpen, setAddToRouteModalOpen] = useState<boolean>(false);
  const [orderIdForModal, setOrderIdForModal] = useState<string>('');
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [multipleOrders, setMultipleOrders] = useState<boolean>(false);

  const fetchOrdersFn = async () => {
    setLoading(true);
    const { page, ...rest } = filters;
    try {
      let filter = {
        ...rest,
      };
      if (props.customAPIParams) {
        filter = { ...props.customAPIParams };
      }
      const response = await fetchOrders({
        page,
        limit: getPageLimit(),
        filter,
      });
      setOrders(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const queryStringToFilter = () => {
    return qs.parse(props.location.search, { parseBooleans: true, parseNumbers: true });
  };

  useEffect(() => {
    filters = {
      ...filters,
      ...queryStringToFilter(),
    };
    if (!props.data) {
      fetchOrdersFn();
    }
  }, []);

  const markOrderPacked = async (orderId: string) => {
    try {
      await markOrderAsPacked(orderId);
      await fetchOrdersFn();
      showSuccess('Order marked as PACKED successfully');
    } catch (e) {
      showError(e);
    }
  };

  const markOrderDelivered = async (orderId: string) => {
    try {
      await markOrderAsDelivered(orderId);
      await fetchOrdersFn();
      showSuccess('Order marked as DELIVERED successfully');
    } catch (e) {
      showError(e);
    }
  };

  const removeFromRoute = async (orderId: string) => {
    try {
      await removeOrderFromRoute(orderId);
      await fetchOrdersFn();
      showSuccess('Order removed from route successfully');
    } catch (e) {
      showError(e);
    }
  };

  const setTooltipDataFn = (data: Record<string, any>) => {
    setTooltipData(data.route);
    setRenderTooltip(true);
  };

  const getOrderActions = (row: Record<string, any>) => {
    const actions: Record<string, any>[] = [];
    if (row.status !== ORDER_STATUS.PACKED || row.status === ORDER_STATUS.PENDING_PAYMENT) {
      actions.push({
        text: 'Mark Packed',
        onClick: () => markOrderPacked(row.objectId),
      });
    }
    if (row.status !== ORDER_STATUS.DELIVERED || row.status === ORDER_STATUS.PENDING_PAYMENT) {
      actions.push({
        text: 'Mark Delivered',
        onClick: () => markOrderDelivered(row.objectId),
      });
    }
    if (!!row.route) {
      actions.push({
        text: 'Remove from Route',
        onClick: async () => {
          await removeFromRoute(row.objectId);
          await props.data && props.refreshData();
        },
      });
    } else {
      actions.push({
        text: 'Add to Route',
        onClick: () => {
          setOrderIdForModal(row.objectId);
          setAddToRouteModalOpen(true);
          setMultipleOrders(false);
        },
      });
    }
    return actions;
  };

  const columns = useMemo(() => [
    {
      name: 'OrderID',
      selector: 'objectId',
      sortable: false,
      width: '120px',
      // eslint-disable-next-line react/display-name
      cell: row => (
        <CustomLink
          text={row.objectId}
          linkTo={`/orders/${row.objectId}`}
        />
      ),
    },
    {
      name: 'Status',
      selector: 'status',
      sortable: false,
      width: '120px',
    },
    {
      name: 'Delivery Method',
      selector: 'selectedDeliveryMethod',
      format: (row) => {
        return row.selectedDeliveryMethod === 'ROUTABLE' ? row.preferredDeliveryDay : row.selectedDeliveryMethod;
      },
      sortable: false,
      width: '170px',
    },
    {
      name: 'Ordered At',
      selector: 'createdAt',
      sortable: false,
      format: (row) => formatDate(row.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A),
      width: '170px',
    },
    {
      name: 'Total',
      selector: 'total',
      sortable: false,
      // eslint-disable-next-line react/display-name
      format: (row) => <Currency amount={row.total / 100} default="-" />,
      width: '120px',
    },
    {
      name: 'Cost',
      selector: 'cost',
      sortable: false,
      // eslint-disable-next-line react/display-name
      format: (row) => <Currency amount={row.cost} default="-" />,
      width: '120px',
    },
    {
      name:'Waiting For Payment',
      selector:'waitingForPayment',
      format :(row)=>  row.waitingForPayment?.toString() ?? '-',
    },
    {
      name: 'Refunded Amount',
      selector: 'refundedAmount',
      sortable: false,
      // eslint-disable-next-line react/display-name
      format: (row) => row.refundedAmount ? (<Currency amount={row.refundedAmount} />) : '-',
      width: '120px',
    },
    {
      name: 'Purchaser Info',
      selector: 'purchaser',
      sortable: false,
      width: '250px',
      // eslint-disable-next-line react/display-name
      cell: (row) => <div style={{ height: 'auto' }}>
        <div style={{ fontSize: '16px' }}>{(row.purchaser.full_name ? row.purchaser.full_name : row.purchaser.username)}</div>
        <div style={{ fontSize: '12px', color: '#8F8F8F' }}>{formatAddress(row.addressObject)}</div>
      </div>,
    },
    {
      name: 'Account Info',
      selector: 'purchaser.account',
      width: '180px',
      // eslint-disable-next-line react/display-name
      cell: (row) => row.purchaser?.account ? (
        <div style={{ height: 'auto' }}>
          <div style={{ fontSize: '12px', color: '#8F8F8F' }}>{row.purchaser.account.objectId}</div>
          <div style={{ fontSize: '16px' }}>{(row.purchaser.full_name || row.purchaser.account.name || '')}</div>
        </div>
      ): '-',
    },
    {
      name: 'Notes',
      selector: 'notes',
      sortable: false,
      format: row => row.notes + row.notes + row.notes + row.notes + row.notes + row.notes + row.notes,
      width: '220px',
      // eslint-disable-next-line react/display-name
      cell: (row) => <div style={{ height: 'auto' }} onClick={() => window.location.assign(`/orders/${row.objectId}`)}>
        {row.notes ? row.notes : '-'}
      </div>,
    },
    {
      name: 'Route ID',
      selector: 'route',
      sortable: false,
      width: '175px',
      // eslint-disable-next-line react/display-name
      cell: (row) => (
        <>
          <div data-for={'route-detail'} data-tip={''}>
            <div
              onClick={() => setTooltipDataFn(row)}
            >
              {row.route?.objectId || row.route?.id || '-'}
            </div>
          </div>
          {renderTooltip && (
            <RouteInfoTooltip routeInfo={tooltipData || null} hideToolTip={() => setRenderTooltip(false)} />
          )}
        </>
      ),
    },
    {
      name: '',
      width: '130px',
      // eslint-disable-next-line react/display-name
      cell: (row) => (
        <RowActionComponent
          actions={getOrderActions(row)}
        />
      ),
    },
  ], [renderTooltip, addToRouteModalOpen, props.dataUpdated]);

  const filterToQueryString = () => {
    return qs.stringify({ ...filters });
  };

  const updateQueryString = () => {
    const qs = filterToQueryString();
    props.history.push({
      pathname: props.location.pathname,
      search: qs,
    });
  };

  const onChangePage = (page: number) => {
    filters.page = page - 1;
    updateQueryString();
    fetchOrdersFn();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  const setSelectedOrders = (data: Record<string, any>) => {
    const { selectedRows } = data;
    setSelectedOrderIds(selectedRows.map(row => row.objectId));
  };

  const onFilterSubmit = (updatedFilter: Record<string, any>) => {
    if(updatedFilter.orderId){
      props.history.push(`/orders/${updatedFilter.orderId}`);
    }else{
      filters = { ...updatedFilter, page: 0 };
      updateQueryString();
      fetchOrdersFn();
    }
  };

  return (
    <div className={`${styles['main-container']}`}>
      <DashboardPage isOrdersListPage/>
      {addToRouteModalOpen && (
        <AddOrderToRouteModal
          orderIds={multipleOrders ? selectedOrderIds : orderIdForModal}
          onSuccess={() => {
            setSelectedOrderIds([]);
            fetchOrdersFn();
            props.data && props.refreshData();
          }}
          toggleModal={() => {
            setOrderIdForModal('');
            setAddToRouteModalOpen(!addToRouteModalOpen);
          }}
        />
      )}
      {(!props.data && !props.hideFilter) && (
        <div className={styles['header']}>
          <div className={styles['heading']}>Orders</div>
          {selectedOrderIds.length ? (
            <ActionButton
              text={'Add Orders To Route'}
              active={true}
              isLoading={false}
              onClick={() => {
                setMultipleOrders(true);
                setAddToRouteModalOpen(true);
              }}
            />
          ): null}
          <ActionButton
            text={<>External Order <span>+</span></>}
            active={true}
            isLoading={false}
            onClick={() => props.history.push('/external-orders/create')}
          />
        </div>
      )}
      {(!props.data && !props.hideFilter) && (
        <Filter {...props} onSubmit={onFilterSubmit} />
      )}
      <div className={styles['data-table']}>
        <DataTable
          keyField="objectId"
          columns={columns}
          noHeader={true}
          data={orders.items ? orders.items : props.data}
          highlightOnHover={true}
          customStyles={tableStyles}
          pagination={true}
          paginationServer={true}
          paginationPerPage={getPageLimit()}
          paginationDefaultPage={(filters.page || 0) + 1}
          paginationTotalRows={props.total || orders.total}
          paginationRowsPerPageOptions={[50, 100, 200]}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          selectableRows={props.data ? props.selectableRows : true}
          onSelectedRowsChange={props.setSelectedOrders || setSelectedOrders}
          selectableRowSelected={row => selectedOrderIds.includes(row.objectId)}
          loading={loading}
        />
      </div>
    </div>
  );
};

export { Orders };
